import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useLocation } from 'react-router';

import type {
  RealtimeChannel,
  RealtimePresenceState,
} from '@supabase/supabase-js';

import { useAuth } from '@/hooks/useAuth';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';

export interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  imageUrl: string;
  location: string;
}

const UserPresenceProviderContext = createContext<{
  listOfUsers: RealtimePresenceState | null;
} | null>(null);

// eslint-disable-next-line max-lines-per-function
export const UserPresenceProvider = ({ children }: PropsWithChildren) => {
  const [listOfUsers, setListOfUsers] = useState<RealtimePresenceState | null>(
    null
  );
  const supabase = useSupabaseClient();
  const location = useLocation();
  const roomRef = useRef<RealtimeChannel | null>(null);
  const locationRef = useRef(location.pathname);
  const subscribedRef = useRef(false);
  locationRef.current = location.pathname;

  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setListOfUsers(null);
      return;
    }

    const room = supabase.channel(`praxis-user-presence`);
    roomRef.current = room;

    room.on('presence', { event: 'sync' }, () => {
      const newState = room.presenceState();
      setListOfUsers(newState);
    });

    room.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        subscribedRef.current = true;
        await room.track({
          user: {
            id: user?.id,
            firstName: user?.firstName,
            lastName: user?.lastName,
            imageUrl: user?.imageUrl,
            location: locationRef.current,
          },
          online_at: new Date().toISOString(),
        });
      }
    });

    return () => {
      subscribedRef.current = false;
      supabase.removeChannel(room);
      roomRef.current = null;
    };
  }, [user, supabase]);

  useEffect(() => {
    const room = roomRef.current;
    if (!room || !user || !subscribedRef.current) {
      return;
    }

    void room.track({
      user: {
        id: user?.id,
        firstName: user?.firstName,
        lastName: user?.lastName,
        imageUrl: user?.imageUrl,
        location: location.pathname,
      },
      online_at: new Date().toISOString(),
    });
  }, [location.pathname, user]);

  return (
    <UserPresenceProviderContext.Provider value={{ listOfUsers }}>
      {children}
    </UserPresenceProviderContext.Provider>
  );
};

export const useUserPresenceProvider = (path: string) => {
  const listOfUsersContext = useContext(UserPresenceProviderContext);

  if (
    listOfUsersContext === undefined ||
    listOfUsersContext?.listOfUsers === undefined
  ) {
    throw new Error(
      'useUserPresenceProvider must be used within a UserPresenceProvider'
    );
  }

  const listOfUsers = listOfUsersContext.listOfUsers;

  if (!listOfUsers) {
    return [];
  }

  return Object.values(listOfUsers)
    .flatMap((list) => list)
    .filter(
      (entry: { presence_ref: string; user: UserProfile }) =>
        entry.user.location === path
    )
    .map((entry: { presence_ref: string; user: UserProfile }) => entry.user);
};
